import { useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Tab,
  TabList,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  DismissRegular,
  HomeRegular,
  RocketRegular,
  DocumentBulletListRegular,
  AlertRegular,
  MailRegular,
  FolderRegular,
  PersonRegular,
  PlugConnectedRegular,
  WrenchRegular,
} from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import { wikiContent } from '../content/wikiContent';

const useStyles = makeStyles({
  drawerBody: {
    padding: 0,
  },
  tabList: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '0 16px',
    flexShrink: 0,
  },
  tab: {
    minHeight: '40px',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
    '& h1': {
      fontSize: '24px',
      fontWeight: 600,
      marginBottom: '16px',
      marginTop: 0,
      color: tokens.colorNeutralForeground1,
    },
    '& h2': {
      fontSize: '20px',
      fontWeight: 600,
      marginTop: '24px',
      marginBottom: '12px',
      color: tokens.colorNeutralForeground1,
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      paddingBottom: '8px',
    },
    '& h3': {
      fontSize: '16px',
      fontWeight: 600,
      marginTop: '20px',
      marginBottom: '8px',
      color: tokens.colorNeutralForeground1,
    },
    '& p': {
      marginBottom: '12px',
      lineHeight: 1.6,
      color: tokens.colorNeutralForeground2,
    },
    '& ul, & ol': {
      marginBottom: '12px',
      paddingLeft: '24px',
      color: tokens.colorNeutralForeground2,
    },
    '& li': {
      marginBottom: '6px',
      lineHeight: 1.5,
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '16px',
      fontSize: '14px',
    },
    '& th': {
      textAlign: 'left',
      padding: '10px 12px',
      backgroundColor: tokens.colorNeutralBackground3,
      borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
      fontWeight: 600,
      color: tokens.colorNeutralForeground1,
    },
    '& td': {
      padding: '10px 12px',
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      color: tokens.colorNeutralForeground2,
    },
    '& tr:hover td': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    '& code': {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: "Consolas, 'Courier New', Courier, monospace",
    },
    '& pre': {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: '16px',
      borderRadius: '8px',
      overflow: 'auto',
      marginBottom: '16px',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& blockquote': {
      borderLeft: `4px solid ${tokens.colorBrandBackground}`,
      margin: '16px 0',
      padding: '8px 16px',
      backgroundColor: tokens.colorNeutralBackground2,
      borderRadius: '0 4px 4px 0',
      '& p': {
        marginBottom: 0,
      },
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
      margin: '24px 0',
    },
    '& strong': {
      fontWeight: 600,
      color: tokens.colorNeutralForeground1,
    },
    '& a': {
      color: tokens.colorBrandForegroundLink,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
});

type WikiSection =
  | 'home'
  | 'getting-started'
  | 'tax-returns'
  | 'notifications'
  | 'outreach'
  | 'documents'
  | 'client-portal'
  | 'integrations'
  | 'troubleshooting';

interface WikiDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const sections: { id: WikiSection; label: string; icon: JSX.Element }[] = [
  { id: 'home', label: 'Overview', icon: <HomeRegular /> },
  { id: 'getting-started', label: 'Getting Started', icon: <RocketRegular /> },
  { id: 'tax-returns', label: 'Tax Returns', icon: <DocumentBulletListRegular /> },
  { id: 'notifications', label: 'Notifications', icon: <AlertRegular /> },
  { id: 'outreach', label: 'Outreach', icon: <MailRegular /> },
  { id: 'documents', label: 'Documents', icon: <FolderRegular /> },
  { id: 'client-portal', label: 'Client Portal', icon: <PersonRegular /> },
  { id: 'integrations', label: 'Integrations', icon: <PlugConnectedRegular /> },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: <WrenchRegular /> },
];

export default function WikiDrawer({ open, onOpenChange }: WikiDrawerProps) {
  const styles = useStyles();
  const [activeSection, setActiveSection] = useState<WikiSection>('home');

  const currentContent = wikiContent[activeSection] || wikiContent.home;

  return (
    <Drawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      size="large"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={() => onOpenChange(false)}
            />
          }
        >
          Help & Documentation
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <TabList
            selectedValue={activeSection}
            onTabSelect={(_, data) => setActiveSection(data.value as WikiSection)}
            className={styles.tabList}
            size="small"
          >
            {sections.map((section) => (
              <Tab
                key={section.id}
                value={section.id}
                icon={section.icon}
                className={styles.tab}
              >
                {section.label}
              </Tab>
            ))}
          </TabList>

          <div className={styles.content}>
            <ReactMarkdown>{currentContent}</ReactMarkdown>
          </div>
        </div>
      </DrawerBody>
    </Drawer>
  );
}
